import React, { useState, useEffect } from 'react';
import Header from '@/components/ui/Header';
import JobCard from './components/JobCard';
import Icon from '@/components/AppIcon';
import Button from '@/components/ui/Button';
import { Calendar, MapPin, Building2, DollarSign, Clock, Eye, Filter, Search, X, ChevronDown, TrendingUp, Briefcase, CheckCircle, Send,Video,Star,Trophy,Medal } from 'lucide-react';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import 'react-toastify/dist/ReactToastify.css';
import apiRequest from '@/services/requests';
import Pagination from './components/Pagination';
import StatsSection from './components/StatsSection';
import FilterSection from './components/FilterSection';
import PageHeader from './components/PageHeader';
import DetailsModal from './components/DetailsModal';

import {LoadingState, EmptyState} from './components/LoadingState';

 

const Applications = () => {
	const navigate = useNavigate();
const [applications, setApplications] = useState([]);
const [isLoading, setIsLoading] = useState(true);
const [currentPage, setCurrentPage] = useState(1);
const [totalPages, setTotalPages] = useState(1);
const [totalResults, setTotalResults] = useState(0);
const [showFilters, setShowFilters] = useState(false);
const [selectedApplication, setSelectedApplication] = useState(null);
const [showDetailsModal, setShowDetailsModal] = useState(false);
const [stats, setStats] = useState({
total:0,
appliedToday:0,
shortlisted:0,
}); 
const [filters, setFilters] = useState({
search: '',
status: 'all',
dateFrom: '',
dateTo: ''
});
const resultsPerPage = 10;


/* ---------- life-cycle ---------- */
useEffect(() => { loadApplications(); }, [currentPage, filters]);

 
/* ---------- data loader ---------- */
const loadApplications = async () => {
setIsLoading(true);
try {
const [listData, statData] = await Promise.all([
apiRequest.applicationsList({ page: currentPage, perPage: resultsPerPage, ...filters }),
apiRequest.applicationStats()
]);
setApplications(listData.applications);
setTotalResults(listData.totalResults);
setTotalPages(listData.totalPages);
setStats(statData.stats);          // ← real counts
} catch (e) {
console.error(e);
toast.error('Could not load applications, please refresh or try again later!',{
position:'bottom-left'
});
} finally {
setIsLoading(false);
}
};


/* ---------- actions ---------- */
const handleStatusChange = async (appId, newStatus) => {
toast(
({ closeToast }) => (
<div>
<p className="text-sm text-gray-800 mb-3">
Are you sure you want to change the status to <span className="text-red-500 capitalize">{newStatus}</span>?
</p>
<div className="flex justify-end gap-2">
<button
onClick={async () => {
try {
await apiRequest.applicationUpdateStatus(appId, newStatus);
toast.success('Status updated');
closeToast();
loadApplications();          // refresh list + stats
} catch {
toast.error('Update failed');
}
}}
className="px-3 py-1 bg-primary text-white rounded text-sm"
>Yes</button>
<button onClick={closeToast} className="px-3 py-1 bg-muted text-sm rounded">Cancel</button>
</div>
</div>
),
{ position: 'bottom-center', autoClose: false, closeOnClick: false, draggable: false, closeButton: false }
);
};


const handleViewDetails = (application) => {
setSelectedApplication(application);
setShowDetailsModal(true);
};

const getStatusConfig = (status) => {
const configs = {
submitted: { label: 'submitted', value:'submitted', color: 'bg-yellow-100 text-yellow-800', icon: Send },
shortlisted: { label: 'shortlisted',value:'shortlisted', color: 'bg-sky-100 text-sky-800', icon: CheckCircle },
interview_scheduled: { label: 'interview scheduled',value:'interview_scheduled', color: 'bg-blue-100 text-blue-800', icon: Video },
interview_completed: { label: 'interview completed',value:'interview_completed', color: 'bg-purple-100 text-purple-800', icon: Video },
rejected: { label: 'rejected',value:'rejected', color: 'bg-red-100 text-red-800', icon: X },
upper_round: { label: 'upper round',value:'upper_round', color: 'bg-orange-100 text-orange-800', icon: Clock },
offer_received: { label: 'offer received', value:'offer_received', color: 'bg-green-800 text-green-100', icon: Medal },
hired: { label: 'hired', value:'hired', color: 'bg-green-100 text-green-800', icon: Trophy },
withdrawn: { label: 'withdrawn', value:'withdrawn', color: 'bg-black text-white', icon: X },
};
return configs[status] || configs.submitted;
};


const getDaysUntilDeadline = (deadline) => {
const today = new Date();
const deadlineDate = new Date(deadline);
const diffTime = deadlineDate - today;
const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
return diffDays;
};


const handlePageChange = (page) => {
setCurrentPage(page);
window.scrollTo({ top: 0, behavior: 'smooth' });
};


const hasActiveFilters = () => {
return filters?.search || 
(filters?.status && filters?.status !== 'any') ||
filters?.dateFrom || filters?.dateTo ;
};


const handleClearFilters = () => {
setFilters({
search: '',
status: 'all',
dateFrom: '',
dateTo: ''
});
setCurrentPage(1);
};


return (
<div className="min-h-screen bg-background">
<Header />

<main className="pt-16">
<div className="max-w-7xl mx-auto px-6 py-8">


<div className="mb-8">

{/* Page Header */}
<PageHeader
loadApplications={loadApplications}
isLoading={isLoading}
navigate={navigate} />
{/* End Page Header */}

{/* Stats Cards */}
<StatsSection stats={stats} />
{/* End Stats Cards */}

</div>




{/* Search and Filter Bar */}
<FilterSection 
filters={filters}
setFilters={setFilters}
setShowFilters={setShowFilters}
showFilters={showFilters}
/>

{/* Applications List */}
<div className="space-y-4">

{isLoading ? ( <LoadingState/> ) : 

applications.length === 0 ? (

<EmptyState 
hasFilters={hasActiveFilters()} 
onClearFilters={handleClearFilters}
/>

) : (
applications.map((app) => {
const statusConfig = getStatusConfig(app.status);
const StatusIcon = statusConfig.icon;
const daysUntilDeadline = getDaysUntilDeadline(app.deadline);

return (
<JobCard
key={app.id}
job={app}
onStatusChange={handleStatusChange}
onClickView={handleViewDetails}
getStatusConfig={getStatusConfig}
/>
);
})
)}
</div>


{/* Pagination */}
<Pagination
currentPage={currentPage}
totalPages={totalPages}
totalResults={totalResults}
resultsPerPage={resultsPerPage}
onPageChange={handlePageChange}
isLoading={isLoading}
/>


{/* Mobile Action Buttons */}
<div className="md:hidden fixed bottom-16 right-6 flex flex-col space-y-3">
<Button
variant="default"
size="icon"
className="w-10 h-10 rounded-full shadow-elevation-2"
onClick={loadApplications}
disabled={isLoading}
>
<Icon name="RefreshCw" size={20} />
</Button>
</div>


</div>
</main>



{/* Details Modal */}
{showDetailsModal && selectedApplication && (

<DetailsModal
setShowDetailsModal={setShowDetailsModal}
selectedApplication={selectedApplication}
getStatusConfig={getStatusConfig}
/>

)}
</div>
);
};

export default Applications;